import {
  ConfigType,
  GridFeeType,
  ListCommunityInfoDocument,
  ListCommunityInfoQuery,
  SpotMarketType,
} from 'src/graphql';
import { EFormVariant, TFieldValue } from 'src/typings/base-types';
import { FormFieldsGenerator, TFormFieldsGeneratorProps } from 'src/components/FormFieldsGenerator';
import React, { useEffect, useState } from 'react';
import {
  TCommunitySettingsFields,
  TFieldsUnionWithValue,
  TFileFieldWithValue,
  TSettingsData,
} from 'src/utils/assetsFields/assetsFields.types';
import {
  TFieldsVisibility,
  TFormSettingsDataProps,
  TSettingsSaveProps,
} from './FormSettingsData.types';
import { configType, isLibrary } from 'src/mocks/configurationSettings';
import { fieldTemplates, validators } from './formFields';
import {
  selectCommunityAssetSettings,
  selectConfigurationCharacteristic,
  selectIsCommunityCreatedInDB,
  selectReadOnly,
  selectSettingsData,
} from 'src/redux/configuration/configuration.selectors';

import { WritableDraft } from 'immer/dist/internal';
import { findField } from 'src/utils/fieldUtils';
import { getAssetValues } from 'src/utils/assetsFields/fieldTemplatesWithValues';
import produce from 'immer';
import { selectIsAdmin } from 'src/redux/auth/auth.selectors';
import { useApolloClient } from '@apollo/client';
import { useCurrentRefs } from 'src/hooks/useCurrentRefs';
import { useSelector } from 'react-redux';
import { validateFields } from 'src/utils/validation';

function getVisibleFields({
  fields,
  fieldsVisibility,
  formVariant,
  isAdmin,
}: {
  fields: ReturnType<typeof fieldTemplates>;
  fieldsVisibility: TFieldsVisibility;
  formVariant: TFormSettingsDataProps['formVariant'];
  isAdmin: boolean;
}) {
  if (!fields) return fields;

  return fields.reduce((acc: ReturnType<typeof fieldTemplates>, field) => {
    const conditionA =
      formVariant === EFormVariant.Express ? field.formView === EFormVariant.Express : true;
    const conditionB = field.name in fieldsVisibility ? fieldsVisibility[field.name] : true;

    if (conditionA && conditionB) {
      if (field.name === 'pvUserProfile') {
        const modifiedField = produce(field, (draftState) => {
          (draftState as WritableDraft<TFileFieldWithValue>).allowDownload = isAdmin;
        });
        acc.push(modifiedField);
      } else {
        acc.push(field);
      }
    }

    return acc;
  }, []);
}

export const FormSettingsData: React.FC<TFormSettingsDataProps> = ({
  formVariant,
  hasErrorsRef,
  onSubmit,
  theme = 'light',
  ...formFieldsGeneratorProps
}) => {
  const client = useApolloClient();

  const isAdmin = useSelector(selectIsAdmin);
  const settingsData = useSelector(selectSettingsData);
  const { gridFeePercentage, gridFeeConstant } = useSelector(selectCommunityAssetSettings) || {};
  const configurationCharacteristic = useSelector(selectConfigurationCharacteristic);
  const isCommunityCreatedInDB = useSelector(selectIsCommunityCreatedInDB);
  const readOnly = useSelector(selectReadOnly);

  const [allFields, setAllFields] = useState(
    fieldTemplates({
      values: {
        ...settingsData,
        name: configurationCharacteristic.name,
        description: configurationCharacteristic.description,
        locationVisible: configurationCharacteristic.locationVisible,
        gridFeeEnabled: Boolean(gridFeePercentage || gridFeeConstant),
        gridFeePercentage,
        gridFeeConstant,
      },
      configurationCharacteristic,
    }),
  );

  const [errors, setErrors] = useState<TFormFieldsGeneratorProps['errors']>(null);

  // If a field is not present in the object it will be visible by default.
  const fieldsVisibility: TFieldsVisibility = {
    currency: !isLibrary,
    startEndDate: !isLibrary,
    pvUserProfile: findField(allFields, 'cloudCoverage')?.value === 4,
    tickLengthSeconds: !isLibrary,
    gridFeeType: !isLibrary,
    slotLengthRealtimeSeconds: configType !== ConfigType.CanaryNetwork && !isLibrary,
    gridFeeEnabled: !isLibrary,
    gridFeePercentage: Boolean(
      !isLibrary &&
        findField(allFields, 'gridFeeEnabled')?.value &&
        findField(allFields, 'gridFeeType')?.value === GridFeeType.Percentage,
    ),
    gridFeeConstant: Boolean(
      !isLibrary &&
        findField(allFields, 'gridFeeEnabled')?.value &&
        findField(allFields, 'gridFeeType')?.value === GridFeeType.Constant,
    ),
    bidOfferMatchAlgo: findField(allFields, 'spotMarketType')?.value === SpotMarketType.TwoSided,
  };

  const visibleFields = getVisibleFields({
    fields: allFields,
    fieldsVisibility,
    formVariant,
    isAdmin,
  });

  function getUsedProjectNames(): string[] {
    const data: ListCommunityInfoQuery | null = client.readQuery({
      query: ListCommunityInfoDocument,
    });
    const projects = data?.listCommunityInfo?.projects || [];
    const output: string[] = [];

    projects.forEach((project) => {
      const name = project?.name;

      if (name) {
        output.push(name);
      }
    });

    return output;
  }

  function validateFieldsWrapper(fields: TFieldsUnionWithValue[]) {
    const output = validateFields({
      validators: validators({
        usedProjectNames: getUsedProjectNames(),
        currentProjectName: isCommunityCreatedInDB ? configurationCharacteristic.name : '',
      }),
      fields,
    });

    setErrors(output.errors);

    return output;
  }

  const handleChange = ({
    name,
    value,
  }: {
    name: keyof TSettingsData | 'locationVisible';
    value: TFieldValue;
  }) => {
    if (!allFields) return;
    // We use immer for immutability, cloneDeep is too expensive
    const newFields = produce(allFields, (draftState) => {
      const fieldToUpdate = draftState.find((f) => f.name === name);

      if (fieldToUpdate) {
        fieldToUpdate.value = value;

        // Update sibling fields
        switch (fieldToUpdate.name) {
          case 'gridFeeEnabled':
          case 'gridFeeType':
            const gridFeeType = draftState.find((f) => f.name === 'gridFeeType')?.value as
              | GridFeeType
              | undefined;

            if (gridFeeType) {
              const gridFeePercentageField = draftState.find((f) => f.name === 'gridFeePercentage');
              const gridFeeConstantField = draftState.find((f) => f.name === 'gridFeeConstant');

              if (gridFeeType === GridFeeType.Constant) {
                if (gridFeePercentageField) {
                  gridFeePercentageField.value = null;
                }
                if (gridFeeConstantField) {
                  gridFeeConstantField.value = 0;
                }
              } else if (gridFeeType === GridFeeType.Percentage) {
                if (gridFeePercentageField) {
                  gridFeePercentageField.value = 0;
                }
                if (gridFeeConstantField) {
                  gridFeeConstantField.value = null;
                }
              }
            }

            break;

          default:
            break;
        }
      }
    });

    validateFieldsWrapper(newFields);

    setAllFields(newFields);
  };

  const currentRefs = useCurrentRefs({ handleChange });

  // useEffect(() => {
  //   if (hasErrorsRef) {
  //     hasErrorsRef.current = Boolean(errors);
  //   }
  // }, [hasErrorsRef, errors]);

  /*
   * Sync locationVisible field's value which can be controlled inside other components e.g. ShareSimulation
   */

  // useEffect(() => {
  //   currentRefs.current.handleChange({
  //     name: 'locationVisible',
  //     value: configurationCharacteristic.locationVisible,
  //   });
  // }, [configurationCharacteristic.locationVisible, currentRefs]);

  return (
    <FormFieldsGenerator
      fields={visibleFields}
      errors={errors}
      onChange={handleChange}
      onSubmit={() => {
        const { errors } = validateFieldsWrapper(allFields);

        if (errors) return;

        const values = getAssetValues(
          allFields.filter((f) => (f.name in fieldsVisibility ? fieldsVisibility[f.name] : true)),
        ) as TCommunitySettingsFields & TSettingsSaveProps['communityAssetSettings'];

        if (values.startEndDate) {
          values.startDate = values.startEndDate.startDate as string;
          values.endDate = values.startEndDate.endDate as string;
        }

        const {
          name,
          description,
          locationVisible,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          startEndDate,
          gridFeeConstant,
          gridFeePercentage,
          ...newSettingsData
        } = values;

        onSubmit({
          name,
          description,
          locationVisible,
          settingsData: newSettingsData,
          communityAssetSettings: { gridFeeConstant, gridFeePercentage },
        });
      }}
      focusField="name"
      theme={theme}
      readOnly={readOnly}
      {...formFieldsGeneratorProps}
    />
  );
};
